import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography, Modal, Grid, IconButton, List, ListItem, ListItemText } from '@mui/material';
import { Button, Search, Dropdown } from '@voicecare.ai/ui-library';
import { message } from 'antd';
import { useSmartOSearch } from 'oreacto';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '../../ThemeContext';
import useExcelUpload from '../../custom-hooks/useExcelUpload';
import useTemplateDownload from '../../custom-hooks/useTemplateDownload';
import { insuranceOptions } from '../../utils/constants/insuranceOptions';

const InNetworkInsuranceField = ({ 
  value = [], 
  onChange, 
  themeMode, 
  hasError, 
  errorMessage 
}) => {
  const { currentTheme } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isManagementModalOpen, setIsManagementModalOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [modalDropdownValue, setModalDropdownValue] = useState(null);
  const fileInputRef = useRef(null);

  // Prepare insurance items for search
  const insuranceItems = Array.isArray(value) ? value.map(insurance => ({ name: insurance })) : [];

  // Use smart search hook for consistency
  const { filteredItems, query, setQuery } = useSmartOSearch({
    items: insuranceItems,
    filterKeys: ["name"],
    searchQuery: "", // initial value
    sortKey: "name",
    sortOrder: "asc",
  });
  
  const { processExcelFile, validateFile, isUploading, uploadError, setUploadError } = useExcelUpload();
  const { downloadTemplate } = useTemplateDownload();

  // Show limited options in dropdown (max 5), remaining can be added via upload
  const visibleOptions = insuranceOptions;
  const selectedCount = Array.isArray(value) ? value.length : 0;
  const shouldShowDropdown = selectedCount <= 5;

  const handleDropdownChange = (selected) => {
    const selectedValues = Array.isArray(selected) 
      ? selected.map(opt => opt.value) 
      : [];
    onChange(selectedValues);
  };

  const handleUploadClick = () => {
    setIsModalOpen(true);
    setUploadedFile(null);
    setUploadError(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setUploadedFile(null);
    setUploadError(null);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const uploadSuccess = await handleFileUpload(file);
      // Auto-process the file immediately if upload was successful
      if (uploadSuccess) {
        await handleProcessFile(file); // Pass file directly
      }
    }
  };

  const handleFileUpload = async (file) => {
    try {
      validateFile(file);
      setUploadedFile(file);
      setUploadError(null);
      return true;
    } catch (error) {
      setUploadError(error.message);
      return false;
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const uploadSuccess = await handleFileUpload(files[0]);
      if (uploadSuccess) {
        await handleProcessFile(files[0]); // Pass file directly
      }
    }
  };

  const handleProcessFile = async (fileToProcess = null) => {
    const fileToUse = fileToProcess || uploadedFile;
    
    if (!fileToUse) {
      setUploadError('No file selected');
      return;
    }

    try {
      const result = await processExcelFile(fileToUse);
      
      if (result.success && result.data && result.data.length > 0) {
        // Merge with existing selected values
        const currentValues = Array.isArray(value) ? value : [];
        const newValues = [...currentValues, ...result.data];
        const uniqueValues = [...new Set(newValues)];

        onChange(uniqueValues);
        
        // Close modal and reset state
        setIsModalOpen(false);
        setUploadedFile(null);
        setUploadError(null);
        
        message.success(`Successfully added ${result.data.length} insurance companies!`);
      } else {
        setUploadError('No valid insurance company names found in the file');
      }
    } catch (error) {
      setUploadError(error.message || 'Failed to process file');
    }
  };

  const handleDownloadTemplate = () => {
    downloadTemplate(['Insurance Company Name'], 'excel', 'insurance-template');
  };

  const handleOpenManagement = () => {
    setIsManagementModalOpen(true);
    setQuery('');
    setModalDropdownValue(null);
  };

  const handleCloseManagement = () => {
    setIsManagementModalOpen(false);
    setQuery('');
    setModalDropdownValue(null);
  };

  const handleModalDropdownChange = (selected) => {
    if (selected && selected.value) {
      const currentValues = Array.isArray(value) ? value : [];
      if (!currentValues.includes(selected.value)) {
        const newValues = [...currentValues, selected.value];
        onChange(newValues);
        message.success('Insurance company added successfully!');
      } else {
        message.warning('This insurance company is already added!');
      }
      // Clear dropdown after selection
      setModalDropdownValue(null);
    }
  };

  const handleRemoveInsurance = (insuranceToRemove) => {
    const currentValues = Array.isArray(value) ? value : [];
    const newValues = currentValues.filter(item => item !== insuranceToRemove);
    onChange(newValues);
  };

  const getFilteredInsuranceList = () => {
    // Use filtered results from oreacto smart search
    return query ? filteredItems.map(item => item.name) : (Array.isArray(value) ? value : []);
  };

  const getCurrentOptions = () => {
    // Combine visible options with any custom values that were added
    const allOptions = [...visibleOptions];
    
    if (Array.isArray(value)) {
      value.forEach(val => {
        if (!allOptions.find(opt => opt.value === val)) {
          allOptions.push({ label: val, value: val });
        }
      });
    }
    
    return allOptions;
  };

  const getAvailableOptions = () => {
    // For modal dropdown - exclude already selected items
    const currentValues = Array.isArray(value) ? value : [];
    return getCurrentOptions().filter(option => !currentValues.includes(option.value));
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    maxWidth: '90vw',
    backgroundColor: currentTheme.callhistory.mainBackground,
    borderRadius: '8px',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflow: 'auto',
    zIndex: 1300 // Ensure modal has proper z-index
  };

  return (
    <Box>
      {shouldShowDropdown ? (
        <>
          {/* Main Dropdown */}
          <Dropdown
            label="In Network Ins.*"
            options={getCurrentOptions()}
            creatable={true}
            placeholder="Network Insurance"
            value={
              Array.isArray(value)
                ? value.map(val => 
                    getCurrentOptions().find(opt => opt.value === val) || 
                    { label: val, value: val }
                  )
                : []
            }
            onChange={handleDropdownChange}
            themeMode={themeMode}
            type="multi-select"
            isSearchable={true}
            width="92%"
            error={hasError}
            helperText={errorMessage}
          />
        </>
      ) : (
        <>
          {/* Management Button for > 20 items */}
          <Box sx={{ width: '92%', mb: 1 }}>
            <Typography 
              variant="body2" 
                              sx={{ 
                  color: currentTheme.text.primary, 
                  mb: 0.3, 
                  fontWeight: 500,
                  fontSize: '0.8125rem'
                }}
            >
              In Network Ins.*
            </Typography>
            <Button
              variant="outlined"
              onClick={handleOpenManagement}
              style={{
                borderRadius: '6px',
                borderColor: currentTheme.border.primary,
                width: '100%',
                textAlign: 'left',
                justifyContent: 'flex-start',
                textTransform: 'none',
                padding: '12px 16px'
              }}
              label={`${selectedCount} insurance companies selected`}
            />
            {hasError && errorMessage && (
              <Typography 
                variant="caption" 
                sx={{ color: '#f44336', mt: 0.5, display: 'block' }}
              >
                {errorMessage}
              </Typography>
            )}
          </Box>
        </>
      )}
      
      {/* Upload Button */}
      <Box sx={{ mt: 1, width: '92%' }}>
        <Button
          variant="outlined"
          onClick={handleUploadClick}
          startIcon={<UploadFileIcon />}
          style={{
            borderRadius: '6px',
            borderColor: currentTheme.border.primary,
            // color: currentTheme.text.primary,
            fontSize: '12px',
            padding: '6px 12px',
            textTransform: 'none'
          }}
          label="Upload Excel"
        />
      </Box>

      {/* Upload Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="upload-modal-title"
      >
        <Box sx={modalStyle}>
          {/* Modal Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            borderBottom: `1px solid ${currentTheme.border.primary}`,
            pb: 2
          }}>
            <Typography 
              id="upload-modal-title" 
              variant="h6" 
              sx={{ color: currentTheme.text.primary }}
            >
              Upload In Network Insurance
            </Typography>
            <IconButton 
              onClick={handleCloseModal}
              sx={{ color: currentTheme.text.primary }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* File Upload Area */}
                     <Box
             sx={{
               border: `2px dashed ${isDragActive ? currentTheme.primary : currentTheme.border.primary}`,
               borderRadius: '8px',
               p: 4,
               textAlign: 'center',
               backgroundColor: isDragActive 
                 ? (themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)')
                 : 'transparent',
               transition: 'all 0.3s ease',
               mb: 3
             }}
             onDragOver={handleDragOver}
             onDragLeave={handleDragLeave}
             onDrop={handleDrop}
           >
            <UploadFileIcon 
              sx={{ 
                fontSize: 48, 
                color: currentTheme.text.secondary, 
                mb: 2 
              }} 
            />
            <Typography 
              variant="h6" 
              sx={{ color: currentTheme.text.primary, mb: 1 }}
            >
              Drag & drop excel (.xls, .xlsx, xlms, xlt ) files to upload
            </Typography>
                         <Typography 
               variant="body2" 
               sx={{ color: currentTheme.text.secondary, mb: 2 }}
             >
               or
             </Typography>
             <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
               <Button
                 variant="contained"
                 startIcon={<UploadFileIcon />}
                 style={{ borderRadius: '6px' }}
                 label="Upload"
                 onClick={() => {
                   fileInputRef.current?.click();
                 }}
               />
             </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx,.xlsm,.xlt,.csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </Box>



          {/* File Status */}
          {isUploading && (
            <Box sx={{ 
              backgroundColor: themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              borderRadius: '6px',
              p: 2,
              mb: 3,
              textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{ color: currentTheme.text.primary }}>
                Processing file...
              </Typography>
            </Box>
          )}
          
          {uploadedFile && !isUploading && (
            <Box sx={{ 
              backgroundColor: themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              borderRadius: '6px',
              p: 2,
              mb: 3
            }}>
              <Typography variant="body2" sx={{ color: currentTheme.text.primary }}>
                Selected file: {uploadedFile.name}
              </Typography>
              <Typography variant="caption" sx={{ color: currentTheme.text.secondary }}>
                Size: {(uploadedFile.size / 1024).toFixed(1)} KB
              </Typography>
            </Box>
          )}

          {/* Error Display */}
          {uploadError && (
            <Box sx={{ 
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              borderRadius: '6px',
              p: 2,
              mb: 3,
              border: '1px solid rgba(244, 67, 54, 0.3)'
            }}>
              <Typography variant="body2" sx={{ color: '#f44336' }}>
                {uploadError}
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                onClick={handleCloseModal}
                style={{
                  borderRadius: '6px',
                  width: '100%',
                  borderColor: currentTheme.border.primary,
                  color: 'white'
                }}
                label="Cancel"
              />
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="contained"
                onClick={handleDownloadTemplate}
                startIcon={<CloudDownloadIcon />}
                style={{
                  borderRadius: '6px',
                  width: '100%'
                }}
                label="Download Template"
              />
            </Grid>
          </Grid>
        </Box>
      </Modal>

      {/* Management Modal for > 20 items */}
      <Modal
        open={isManagementModalOpen}
        onClose={handleCloseManagement}
        aria-labelledby="management-modal-title"
      >
        <Box sx={modalStyle}>
          {/* Modal Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            borderBottom: `1px solid ${currentTheme.border.primary}`,
            pb: 2
          }}>
            <Typography 
              id="management-modal-title" 
              variant="h6" 
              sx={{ color: currentTheme.text.primary }}
            >
              In-Network Insurance
            </Typography>
            <IconButton 
              onClick={handleCloseManagement}
              sx={{ color: currentTheme.text.primary }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Bar */}
          <Box sx={{ width: '100%', mb: 2 }}>
            <Search
              value={query}
              onChange={setQuery}
              themeMode={themeMode}
              currentTheme={currentTheme}
              style={{ width: "92.6%" }}
              placeholder="Search insurance companies"
              fontSize=".9rem"
            />
          </Box>

          {/* Add Insurance Dropdown */}
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: currentTheme.text.primary, 
                mb: 1, 
                fontWeight: 500 
              }}
            >
              Add Insurance Company
            </Typography>
            <Box sx={{ width: '100%' }}>
              <Dropdown
                options={getAvailableOptions()}
                creatable={true}
                placeholder="Select or create insurance company"
                value={modalDropdownValue}
                onChange={handleModalDropdownChange}
                themeMode={themeMode}
                isSearchable={true}
                width="100%"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </Box>
          </Box>

          {/* Insurance List */}
          <Box sx={{ 
            maxHeight: '300px', 
            overflow: 'auto',
            mb: 3,
            '&::-webkit-scrollbar': {
              width: '2px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: themeMode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              borderRadius: '1px',
              '&:hover': {
                background: themeMode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              },
            },
          }}>
            <List>
              {getFilteredInsuranceList().map((insurance, index) => (
                <ListItem 
                  key={index}
                  sx={{
                    borderBottom: `1px solid ${currentTheme.border.primary}`,
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <ListItemText 
                    primary={insurance}
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        color: currentTheme.text.primary 
                      }
                    }}
                  />
                  <IconButton 
                    onClick={() => handleRemoveInsurance(insurance)}
                    sx={{ color: '#f44336' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Action Buttons */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                onClick={handleCloseManagement}
                style={{
                  borderRadius: '6px',
                  width: '100%',
                  borderColor: currentTheme.border.primary,
                }}
                label="Cancel"
              />
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="contained"
                onClick={handleCloseManagement}
                style={{
                  borderRadius: '6px',
                  width: '100%'
                }}
                label="Update"
              />
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Box>
  );
};

export default InNetworkInsuranceField; 